'use client';

import { useCallback, useEffect, useMemo, useRef } from 'react';
import {
  useAppDispatch,
  useAppSelector,
  selectTempFilterSet,
  selectAppliedFilterSet,
  selectTempDateRange,
  selectAppliedDateRange,
  selectApiFilterFormat,
  selectSelectedFilterPreset,
  selectTempSelectedFilterPreset,
  selectLocationSelectedFilterPreset,
  updateFilter,
  removeFilterValue,
  clearFilter,
  applyFilters,
  resetToApplied,
  clearAll,
  setTempFilterSets,
  setTempDateRange,
  saveCurrentToLocation,
  setSelectedFilterPreset,
  setTempSelectedFilterPreset,
  clearSelectedFilterPreset,
  clearTempSelectedFilterPreset,
} from 'src/redux';
import { useLogging } from 'src/context/logging';
import {
  ParentFilterType,
  ParentFilterGroup,
  FilterSet,
  FilterValue,
  DateRange,
} from 'src/context/reducers/filterSetReducer';
import {
  COUNTABLE_FILTER_KEYS,
  FILTER_TYPE_MAPPING,
  LEGACY_FILTER_KEYS,
  PARENT_FILTER_MAPPING,
  defaultFilterValue,
  UseFilterSetsReturn,
} from './types';
import { FilterType } from 'src/context/reducers/filterSetTypes';
import {
  splitSiteOfCareValues,
  mergeSocRowsToOptions,
} from 'src/components/FilterSets/SiteOfCare/utils';
import useSocOptions from 'src/components/FilterSets/SiteOfCare/useSocOptions';

/**
 * Custom hook for managing filter sets across different locations.
 * This version uses Redux for state management.
 *
 * @returns {UseFilterSetsReturn} Object containing filter state and management functions
 * @example
 * const {
 *   tempFilterSet,
 *   appliedFilterSet,
 *   applyFilters,
 *   isDirty
 * } = useFilterSets();
 */
const useFilterSets = (location: string): UseFilterSetsReturn => {
  const log = useLogging();
  const dispatch = useAppDispatch();
  const { socOptions } = useSocOptions();

  // Holds raw SOC values from a preset that was loaded before the SOC hierarchy
  // resolved; the useEffect below merges them once `socOptions` arrives. Without
  // this, `mergeSocRowsToOptions` would fall back to id-only values that
  // `splitSiteOfCareValues` later misclassifies as SOC_TYPE.
  const pendingSocMergeRef = useRef<{
    socTypeValues: string[];
    socSubtypeValues: string[];
    isExclude: boolean;
    isAll: boolean;
  } | null>(null);

  // Don't automatically activate location - let drawers activate when they open

  // Get filter sets and date ranges from Redux
  const tempFilterSet = useAppSelector(selectTempFilterSet);
  const appliedFilterSet = useAppSelector(selectAppliedFilterSet);
  const tempDateRange = useAppSelector(selectTempDateRange);
  const appliedDateRange = useAppSelector(selectAppliedDateRange);
  const rawApiFilterFormat = useAppSelector(selectApiFilterFormat);

  // Site of Care is the only active taxonomy, so always strip legacy Place of
  // Service rows from outgoing queries. Saved filter sets from the backend may
  // still carry PLACE_OF_SERVICE rows; this stops them from leaking into results.
  const apiFilterFormat = useMemo(() => {
    const strippedTypes: string[] = LEGACY_FILTER_KEYS.map(
      (filterKey) => FILTER_TYPE_MAPPING[filterKey]
    );
    return rawApiFilterFormat.map((group) => ({
      ...group,
      filters: group.filters.filter((f) => !strippedTypes.includes(f.type)),
    }));
  }, [rawApiFilterFormat]);
  const selectedFilterPreset = useAppSelector(selectSelectedFilterPreset);
  const globalTempSelectedFilterPreset = useAppSelector(
    selectTempSelectedFilterPreset
  );
  const locationSelectedFilterPreset = useAppSelector(
    selectLocationSelectedFilterPreset(location)
  );
  // Use global temp preset if available, otherwise fall back to location storage
  const tempSelectedFilterPreset =
    globalTempSelectedFilterPreset || locationSelectedFilterPreset;

  /**
   * Checks if a specific filter type has unsaved changes
   * @param {keyof FilterSet} type - The filter type to check
   * @returns {boolean} True if the filter has unsaved changes
   */
  const isFilterDirty = useCallback(
    (type: keyof FilterSet) => {
      return (
        JSON.stringify(tempFilterSet[type]) !==
        JSON.stringify(appliedFilterSet[type])
      );
    },
    [tempFilterSet, appliedFilterSet]
  );

  const getTotalFilterCount = useCallback(() => {
    return COUNTABLE_FILTER_KEYS.reduce((total, filterKey) => {
      const values = tempFilterSet?.[filterKey]?.values;
      return total + (values?.length ? 1 : 0);
    }, 0);
  }, [tempFilterSet]);

  const getFiltersByParentType = useCallback(
    (parentType: ParentFilterType) => {
      const childFilterKeys = PARENT_FILTER_MAPPING[parentType] || [];

      // Return a simple object for API consumption, not the complex ParentFilterGroup
      const filters: Array<{
        type: FilterType;
        is_exclude: boolean;
        is_all: boolean;
        values: string[];
      }> = [];

      childFilterKeys.forEach((filterKey) => {
        const filterValue = tempFilterSet[filterKey];
        if (filterKey === 'siteOfCare') {
          if (filterValue.values.length === 0) return;
          const { socType, socSubtype } = splitSiteOfCareValues(
            filterValue.values
          );

          if (socType.length > 0) {
            filters.push({
              type: FilterType.SOC_TYPE,
              is_exclude: filterValue.is_excluded,
              is_all: filterValue.is_all,
              values: socType,
            });
          }

          if (socSubtype.length > 0) {
            filters.push({
              type: FilterType.SOC_SUBTYPE,
              is_exclude: filterValue.is_excluded,
              is_all: filterValue.is_all,
              values: socSubtype,
            });
          }
          return;
        }
        if (filterValue.values.length === 0) return;
        filters.push({
          type: FILTER_TYPE_MAPPING[filterKey],
          is_exclude: filterValue.is_excluded,
          is_all: filterValue.is_all,
          values: filterValue.values.map((v) => String(v.value)),
        });
      });

      return {
        label: `${parentType} Filters`,
        type: parentType,
        scope: 'personal',
        filters,
      };
    },
    [tempFilterSet]
  );

  const updateFilterHandler = useCallback(
    (type: keyof FilterSet, value: FilterValue) => {
      dispatch(updateFilter({ type, value }));
    },
    [dispatch]
  );

  const removeFilterValueHandler = useCallback(
    (type: keyof FilterSet, label: string) => {
      dispatch(removeFilterValue({ type, label }));
    },
    [dispatch]
  );

  const clearFilterHandler = useCallback(
    (type: keyof FilterSet) => {
      dispatch(clearFilter({ type }));
    },
    [dispatch]
  );

  const clearAllFilters = useCallback(
    (parentType: ParentFilterType) => {
      dispatch(clearAll(parentType));
    },
    [dispatch]
  );

  const updateDateRangeHandler = useCallback(
    (dateRange: DateRange) => {
      dispatch(setTempDateRange(dateRange));
    },
    [dispatch]
  );

  const applyFiltersHandler = useCallback(() => {
    try {
      dispatch(applyFilters());
      // Save the applied state back to location-specific storage
      dispatch(saveCurrentToLocation(location));
    } catch (error) {
      log.exception(`Failed to save filters: ${error}`);
    }
  }, [dispatch, log, location]);

  const resetToAppliedHandler = useCallback(() => {
    dispatch(resetToApplied());
  }, [dispatch]);

  const isDirtyHandler = useCallback(() => {
    return (
      JSON.stringify(tempFilterSet) !== JSON.stringify(appliedFilterSet) ||
      JSON.stringify(tempDateRange) !== JSON.stringify(appliedDateRange)
    );
  }, [tempFilterSet, appliedFilterSet, tempDateRange, appliedDateRange]);

  const setTempFilterSetsHandler = useCallback(
    (filterSets: FilterSet[]) => {
      dispatch(setTempFilterSets(filterSets));
    },
    [dispatch]
  );

  const convertParentFilterSetToFilterSet = useCallback(
    (parentFilterSet: ParentFilterGroup) => {
      // Start with the current tempFilterSet instead of defaultFilterSet
      const newFilterSet = { ...tempFilterSet };

      // Find which parent type this filter set belongs to
      const parentType = parentFilterSet.type;

      // Get the filter keys that belong to this parent type
      const filterKeysForThisParentType =
        PARENT_FILTER_MAPPING[parentType] || [];

      // First, reset only the filters that belong to this parent type
      filterKeysForThisParentType.forEach((filterKey) => {
        newFilterSet[filterKey] = { ...defaultFilterValue };
      });

      // SOC_TYPE and SOC_SUBTYPE are persisted as two separate rows but
      // collapse into one `siteOfCare` UI filter; accumulate and merge at the end.
      // Precedence: SOC_TYPE's is_exclude/is_all always win when present; we
      // fall back to SOC_SUBTYPE's flags only when SOC_TYPE is absent. The two
      // rows always share flags in practice (transformToApiFormat writes them
      // from the same UI filter), so this only matters if the backend ever
      // returns divergent flags — in which case we silently drop SOC_SUBTYPE's.
      let socTypeValues: string[] = [];
      let socSubtypeValues: string[] = [];
      let socIsExclude = false;
      let socIsAll = false;
      let socTypeSeen = false;
      let socSeen = false;

      parentFilterSet?.detailed_filters?.forEach((filter) => {
        if (filter.type === FilterType.SOC_TYPE) {
          socTypeValues = filter.values ?? [];
          socIsExclude = filter.is_exclude;
          socIsAll = filter.is_all;
          socTypeSeen = true;
          socSeen = true;
          return;
        }

        if (filter.type === FilterType.SOC_SUBTYPE) {
          socSubtypeValues = filter.values ?? [];
          if (!socTypeSeen) {
            socIsExclude = filter.is_exclude;
            socIsAll = filter.is_all;
          }
          socSeen = true;
          return;
        }

        // Find the corresponding FilterSet key for this filter type
        const filterSetKey = Object.entries(FILTER_TYPE_MAPPING).find(
          ([, type]) => type === filter.type
        )?.[0] as keyof FilterSet;

        if (filterSetKey) {
          // Use detailed_values if it exists and has data, otherwise use values
          const sourceValues =
            filter.detailed_values && filter.detailed_values.length > 0
              ? filter.detailed_values
              : filter.values.map((value) => ({ value: value, label: value }));

          newFilterSet[filterSetKey] = {
            type: FILTER_TYPE_MAPPING[filterSetKey],
            is_excluded: filter.is_exclude,
            is_all: filter.is_all,
            values: sourceValues,
          };
        }
      });

      if (socSeen) {
        if (socOptions.length === 0) {
          // Defer the merge: id-only fallback values would round-trip as
          // SOC_TYPE on Apply. The useEffect below applies them once
          // `socOptions` resolves.
          pendingSocMergeRef.current = {
            socTypeValues,
            socSubtypeValues,
            isExclude: socIsExclude,
            isAll: socIsAll,
          };
        } else {
          pendingSocMergeRef.current = null;
          newFilterSet.siteOfCare = {
            type: FilterType.SOC_TYPE,
            is_excluded: socIsExclude,
            is_all: socIsAll,
            values: mergeSocRowsToOptions(
              socTypeValues,
              socSubtypeValues,
              socOptions
            ),
          };
        }
      }

      // Only update the temporary filter sets, don't apply them
      // This ensures filters are only applied when the user clicks the Apply button
      dispatch(setTempFilterSets([newFilterSet]));

      // Store the selected preset in the temporary state
      // It will be applied when the user clicks the Apply button
      if (parentType === 'FILTER') {
        dispatch(setTempSelectedFilterPreset(parentFilterSet));
      }
    },
    [dispatch, tempFilterSet, socOptions]
  );

  useEffect(() => {
    const pending = pendingSocMergeRef.current;
    if (!pending || socOptions.length === 0) return;
    pendingSocMergeRef.current = null;
    dispatch(
      updateFilter({
        type: 'siteOfCare',
        value: {
          type: FilterType.SOC_TYPE,
          is_excluded: pending.isExclude,
          is_all: pending.isAll,
          values: mergeSocRowsToOptions(
            pending.socTypeValues,
            pending.socSubtypeValues,
            socOptions
          ),
        },
      })
    );
  }, [socOptions, dispatch]);

  // Clear all filters for a specific parent type (when preset is cleared)
  const clearFiltersForParentType = useCallback(
    (parentType: ParentFilterType) => {
      const newFilterSet = { ...tempFilterSet };
      const filterKeysForThisParentType =
        PARENT_FILTER_MAPPING[parentType] || [];

      // Reset all filters for this parent type to default values
      filterKeysForThisParentType.forEach((filterKey) => {
        newFilterSet[filterKey] = { ...defaultFilterValue };
      });

      dispatch(setTempFilterSets([newFilterSet]));

      // Also clear the temp preset selection
      if (parentType === 'FILTER') {
        dispatch(setTempSelectedFilterPreset(null));
      }
    },
    [dispatch, tempFilterSet]
  );

  // Function to set the selected filter preset
  const setSelectedFilterPresetHandler = useCallback(
    (preset: ParentFilterGroup | null) => {
      dispatch(setSelectedFilterPreset(preset));
    },
    [dispatch]
  );

  // Function to set the temporary selected filter preset
  const setTempSelectedFilterPresetHandler = useCallback(
    (preset: ParentFilterGroup | null) => {
      dispatch(setTempSelectedFilterPreset(preset));
    },
    [dispatch]
  );

  // Function to clear the selected filter preset
  const clearSelectedFilterPresetHandler = useCallback(() => {
    dispatch(clearSelectedFilterPreset());
  }, [dispatch]);

  // Function to clear the temporary selected filter preset
  const clearTempSelectedFilterPresetHandler = useCallback(() => {
    dispatch(clearTempSelectedFilterPreset());
  }, [dispatch]);

  // Alias for consistency with the applied/temp pattern used throughout the app
  const appliedFilterPreset = selectedFilterPreset;

  return {
    apiFilterFormat,
    appliedDateRange,
    appliedFilterSet,
    filterSet: tempFilterSet,
    tempDateRange,
    tempFilterSet,
    selectedFilterPreset,
    tempSelectedFilterPreset,
    appliedFilterPreset,
    applyFilters: applyFiltersHandler,
    clearAllFilters,
    clearFilter: clearFilterHandler,
    convertParentFilterSetToFilterSet,
    clearFiltersForParentType,
    getFiltersByParentType,
    getTotalFilterCount,
    isDirty: isDirtyHandler,
    isFilterDirty,
    removeFilterValue: removeFilterValueHandler,
    resetToApplied: resetToAppliedHandler,
    updateDateRange: updateDateRangeHandler,
    setTempFilterSets: setTempFilterSetsHandler,
    updateFilter: updateFilterHandler,
    setSelectedFilterPreset: setSelectedFilterPresetHandler,
    setTempSelectedFilterPreset: setTempSelectedFilterPresetHandler,
    clearSelectedFilterPreset: clearSelectedFilterPresetHandler,
    clearTempSelectedFilterPreset: clearTempSelectedFilterPresetHandler,
  };
};

export default useFilterSets;
